import { useEffect, useRef, useState } from "react";
import { useAsync } from "../hooks/useAsync";
import Icon from "../Icon";
import { Spinner } from "../Spinner";
import "../../styles/search/search.css";
import { ButtonInput } from "../inputs/ButtonInput";
import { ExpandingTooltipReturn } from "../hooks/useAdvancedTooltip";

export { Search };

type Props<T> = {
  /**
   * Default to empty string
   */
  defaultFilter?: string;
  placeholder?: string;
  searchOnClear?: boolean;
  searchOnMount?: boolean;
  searchMethod: (filter: string) => Promise<T>;
  /**
   * @param result if result is null, search result has been cleared
   */
  onSearch: (result: T | null, filter: string) => void;

  tProps?: ExpandingTooltipReturn["triggerProps"];
};

const Search = <T,>(props: Props<T>) => {
  const mounted = useRef(false);
  const [filter, setFilter] = useState(props.defaultFilter ?? "");
  const abortRef = useRef<AbortController | undefined>(undefined);

  // searchResults == undefined means nothing has been searched
  const [searchResults, search, searching, setSearchResults] = useAsync<
    T | undefined
  >(async (): Promise<T | undefined> => {
    // abort previous call
    abortRef.current?.abort();
    const currController = new AbortController();
    // assign so next call can abort this call
    abortRef.current = currController;

    // call search method
    const result = await props.searchMethod(filter);

    // check if controller aborted after processing
    if (currController.signal.aborted) {
      return undefined;
    }
    return result;
  }, undefined);

  useEffect(() => {
    if (mounted.current) {
      if (
        (filter !== "" || (filter === "" && props.searchOnClear)) &&
        searchResults
      ) {
        props.onSearch(searchResults, filter);
      } else {
        props.onSearch(null, filter);
      }
    } else if (props.searchOnMount) {
      search();
    }
    mounted.current = true;
  }, [searchResults]);

  return (
    <div className={"search"}>
      <ButtonInput
        tProps={props.tProps}
        placeholder={props.placeholder ?? "Search..."}
        value={filter}
        onChange={(e) => {
          if (e.target.value === "") {
            setSearchResults(undefined);
          }
          setFilter(e.target.value);
          if (e.target.value === "" && props.searchOnClear) {
            search();
          }
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            if (filter.length > 0) {
              search();
            } else if (props.searchOnClear) {
              setSearchResults(undefined);
              search();
            } else if (searchResults) {
              // clear old result, search null
              props.onSearch(null, filter);
              setSearchResults(undefined);
            }
          }
        }}
      >
        {searching && <Spinner />}

        {!searchResults && !searching && filter !== "" && (
          <button
            onClick={() => {
              search();
            }}
          >
            <Icon altText={"Search"} icon={"search"} />
          </button>
        )}

        {searchResults && !searching && filter !== "" && (
          <button
            onClick={() => {
              setFilter("");
              setSearchResults(undefined);
            }}
          >
            <Icon altText={"Clear"} icon={"times"} />
          </button>
        )}
      </ButtonInput>
    </div>
  );
};
